<template>
    <div
        class="google-sign-in"
        @click.prevent.stop="emit('click')"
    >
        <div
            ref="googleSignInButtonEl"
            class="sign-in-element"
            @click="trigger"
        ></div>

        <div class="visual">
            <svg
                class="icon"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 48 48"
            >
                <g>
                    <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                    ></path>
                    <path
                        fill="#4285F4"
                        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                    ></path>
                    <path
                        fill="#FBBC05"
                        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                    ></path>
                    <path
                        fill="#34A853"
                        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                    ></path>
                    <path
                        fill="none"
                        d="M0 0h48v48H0z"
                    ></path>
                </g>
            </svg>

            <span class="label">
                {{ t('registration.google_sign_in') }}
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useScriptTag } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { useGoogleSignInId } from '@/plugins/google-sign-in-id'

const emit = defineEmits<{
    submit: []
    fail: []
    click: []
}>()

const { t } = useI18n()

const googleSignInButtonEl = useTemplateRef('googleSignInButtonEl')

const trigger = () => {
    const buttonContainer = googleSignInButtonEl.value

    if (!buttonContainer) {
        return
    }

    const button = buttonContainer.querySelector(
        'div[role="button"]'
    ) as HTMLButtonElement | null

    button?.click()
}

const { googleSignInToken } = storeToRefs(useCustomerRegistrationInputStore())
const client_id = useGoogleSignInId() || ''

useScriptTag(
    'https://accounts.google.com/gsi/client',
    () => {
        if (!googleSignInButtonEl.value) {
            return
        }

        google.accounts.id.initialize({
            use_fedcm_for_button: true,
            client_id,
            callback: (response) => {
                if (response.credential) {
                    googleSignInToken.value = response.credential

                    new Promise(() => emit('submit'))
                        .catch(() => {})
                        .finally(() => {
                            googleSignInToken.value = null
                        })
                } else {
                    emit('fail')
                }
            },
        })
        google.accounts.id.renderButton(googleSignInButtonEl.value, {
            type: 'standard',
            text: 'continue_with',
            shape: 'pill',
            logo_alignment: 'left',
        })
    },
    {
        async: true,
    }
)
</script>

<style scoped lang="scss">
.google-sign-in {
    position: relative;
    width: 100%;
    height: 40px;
    background-color: $white;

    border-radius: 20px;
    border: 1px solid $slate;

    font-family: $effra-font-stack;
    letter-spacing: 0.05em;
    font-size: 1rem;
    font-weight: 400;
    user-select: none;

    .visual {
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        cursor: pointer;

        .icon {
            margin-left: 12px;
            width: 18px;
            height: 18px;
        }

        .label {
            display: block;
            width: 100%;
            text-align: center;
        }
    }

    .sign-in-element {
        opacity: 0;
    }
}
</style>
